import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, message, Upload, Image } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getProducts, createProduct, updateProduct, deleteProduct, createBulkProducts } from '../../../redux/actions/product';
import { getCategories } from '../../../redux/actions/category';
import { CloudUploadOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import ProtectedLayout from '../../../layouts/ProtectedLayout';
import type { UploadFile } from 'antd/es/upload/interface';
import BulkExcelPasteTable from '../Shared/BulkInsertFromExcel';
import { columnMappings } from '../Shared/utility';
import ExportExcel from '../Shared/ExportExcel';

const Products = () => {
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState<any>(null);
    const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const dispatch = useDispatch();
    const { products, totalPages, currentPage } = useSelector((state: any) => state.products);
    const { categories } = useSelector((state: any) => state.categories);

    useEffect(() => {
        dispatch<any>(getProducts());
        dispatch<any>(getCategories());
    }, [dispatch]);

    const showModal = (product?: any) => {
        if (product) {
            setEditingProduct(product);
            form.setFieldsValue({
                ...product,
                category: product.category._id
            });
            // Mevcut resimleri fileList'e ekle
            if (product.images) {
                const files = product.images.map((image: string, index: number) => ({
                    uid: `-${index}`,
                    name: `image-${index}`,
                    status: 'done',
                    url: `${image}`
                }));
                setFileList(files);
            }
        } else {
            setEditingProduct(null);
            form.resetFields();
            setFileList([]);
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingProduct(null);
        setFileList([]);
    };

    const handleSubmit = async (values: any) => {
        try {
            const formData = new FormData();

            // Resim dosyalarını ekle
            fileList.forEach((file: any) => {
                if (file.originFileObj) {
                    formData.append('images', file.originFileObj);
                }
            });

            // Dimensions objesini ayrı ayrı ekle
            if (values.dimensions) {
                formData.append('dimensions[width]', values.dimensions.width);
                formData.append('dimensions[height]', values.dimensions.height);
                formData.append('dimensions[depth]', values.dimensions.depth);
            }

            // Diğer form verilerini ekle
            Object.keys(values).forEach(key => {
                if (key !== 'images' && key !== 'dimensions') {
                    formData.append(key, values[key]);
                }
            });

            if (editingProduct) {
                await dispatch<any>(updateProduct(editingProduct._id, formData));
                message.success('Ürün başarıyla güncellendi');
            } else {
                await dispatch<any>(createProduct(formData));
                message.success('Ürün başarıyla oluşturuldu');
            }

            handleCancel();
        } catch (error) {
            message.error('Bir hata oluştu');
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await dispatch<any>(deleteProduct(id));
            message.success('Ürün başarıyla silindi');
        } catch (error) {
            message.error('Bir hata oluştu');
        }
    };

    const columns: any = [
        {
            title: 'Resim',
            dataIndex: 'featuredImage',
            key: 'featuredImage',
            render: (text: string, record: any) => (
                <Image src={`${record.featuredImage}`} width={100} height={100} className='rounded-md shadow-lg object-fill' />
            )
        },
        {
            title: 'Ürün Adı',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: any, b: any) => a.name.localeCompare(b.name),
            filterSearch: true,
            filters: products.map((product: any) => ({
                text: product.name,
                value: product.name,
            })),
            onFilter: (value: string, record: any) => record.name.includes(value),
        },
        {
            title: 'Ürün Kodu',
            dataIndex: 'code',
            key: 'code',
            sorter: (a: any, b: any) => a.code.localeCompare(b.code),
            filterSearch: true,
            filters: products.map((product: any) => ({
                text: product.code,
                value: product.code,
            })),
            onFilter: (value: string, record: any) => record.code.includes(value),
        },
        {
            title: 'Kategori',
            dataIndex: ['category', 'name'],
            key: 'category',
            sorter: (a: any, b: any) => {
                const categoryA = categories?.find((c: any) => c._id === a.category?._id);
                const categoryB = categories?.find((c: any) => c._id === b.category?._id);
                return (categoryA?.name || '').localeCompare(categoryB?.name || '');
            },
            filters: categories?.map((category: any) => ({
                text: category.name,
                value: category._id,
            })) || [],
            onFilter: (value: string, record: any) => record.category?._id === value,
            render: (text: string, record: any) => {
                const category = categories?.find((c: any) => c._id === record.category?._id);
                return category ? category.name : 'Bilinmiyor';
            }
        },
        {
            title: 'Normal Fiyat',
            dataIndex: 'regularPrice',
            key: 'regularPrice',
            sorter: (a: any, b: any) => (a.regularPrice || 0) - (b.regularPrice || 0),
            render: (price: number) => <div className='text-gray-500 line-through'>
                ₺{(price || 0).toFixed(2)}
            </div>,
        },
        {
            title: 'İndirimli Fiyat',
            dataIndex: 'discountedPrice',
            key: 'discountedPrice',
            sorter: (a: any, b: any) => (a.discountedPrice || 0) - (b.discountedPrice || 0),
            render: (price: number) => price ? (
                <div className='text-green-600'>
                    ₺{price.toFixed(2)}
                </div>
            ) : null,
        },
        {
            title: 'Ebatlar',
            render: (text: string, record: any) => (
                <div>
                    {record.dimensions.width}/{record.dimensions.height}/{record.dimensions.depth}
                </div>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 200,
            align: 'right',
            render: (_: any, record: any) => (
                <div className="space-x-2">
                    <Button type="primary" onClick={() => showModal(record)}>
                        Düzenle
                    </Button>
                    <Button type="primary" danger shape="circle" icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
                </div>
            ),
        },
    ];

    const categoriesOptions = () => {
        return categories?.map((category: any) => ({
            label: category.name,
            value: category._id,
        }));
    }

    return (
        <ProtectedLayout>
            <BulkExcelPasteTable
                modelFields={columns}
                isVisible={isBulkModalVisible}
                onClose={() => setIsBulkModalVisible(false)}
                onSubmit={(data) => {
                    const formData = new FormData();
                    data.forEach((item: any) => {
                        formData.append('products', JSON.stringify(item));
                    });
                    dispatch(createBulkProducts(formData));
                    setIsBulkModalVisible(false);
                }}
            />
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Ürünler</h1>
                    <Button
                        type="primary"
                        onClick={() => setIsBulkModalVisible(true)}
                        className="mr-2"
                        icon={<CloudUploadOutlined />}
                    >
                        Toplu Veri Yükle
                    </Button>
                    <ExportExcel excelData={products} fileName={"Ürünler Listesi"} columnMappings={columnMappings(columns)} />
                    <Button type="primary" onClick={() => showModal()}>
                        Yeni Ürün Ekle
                    </Button>
                </div>
                <Table
                    columns={columns as any}
                    dataSource={products}
                    rowKey="_id"
                    pagination={{
                        total: totalPages * 10,
                        current: currentPage,
                        pageSize: 10,
                    }}
                />

                <Modal
                    title={editingProduct ? "Ürün Düzenle" : "Yeni Ürün Ekle"}
                    open={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                    width={800}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <div className="grid grid-cols-2 gap-4">
                            <Form.Item
                                name="name"
                                label="Ürün Adı"
                                rules={[{ required: true, message: 'Lütfen ürün adını girin' }]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="code"
                                label="Ürün Kodu"
                                rules={[{ required: true, message: 'Lütfen ürün kodunu girin' }]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="category"
                                label="Kategori"
                                rules={[{ required: true, message: 'Lütfen kategori seçin' }]}
                                className="w-full relative"
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    options={categoriesOptions()}
                                    onChange={(value) => form.setFieldsValue({ category: value })}
                                />
                            </Form.Item>

                            <Form.Item
                                name="regularPrice"
                                label="Normal Fiyat"
                                rules={[{ required: true, message: 'Lütfen normal fiyatı girin' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item
                                name="discountedPrice"
                                label="İndirimli Fiyat"
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item
                                name={['dimensions', 'width']}
                                label="Taban Genişliği"
                                rules={[{ required: true, message: 'Lütfen genişliği girin' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item
                                name={['dimensions', 'height']}
                                label="Yanak Yüksekliği"
                                rules={[{ required: true, message: 'Lütfen yüksekliği girin' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item
                                name={['dimensions', 'depth']}
                                label="Jant Çapı"
                                rules={[{ required: true, message: 'Lütfen derinliği girin' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </div>

                        <Form.Item
                            name="description"
                            label="Açıklama"
                            rules={[{ required: true, message: 'Lütfen açıklama girin' }]}
                        >
                            <Input.TextArea rows={4} />
                        </Form.Item>

                        <Form.Item
                            label="Ürün Resimleri"
                            name="images"
                            rules={[{ required: true, message: 'Lütfen en az bir resim yükleyin' }]}
                        >
                            <Upload
                                listType="picture-card"
                                fileList={fileList}
                                onChange={({ fileList }) => setFileList(fileList)}
                                beforeUpload={() => false}
                                multiple
                                maxCount={5}
                            >
                                {fileList.length >= 5 ? null : (
                                    <div>
                                        <UploadOutlined />
                                        <div style={{ marginTop: 8 }}>Yükle</div>
                                    </div>
                                )}
                            </Upload>
                        </Form.Item>

                        <div className="flex justify-end space-x-2">
                            <Button onClick={handleCancel}>
                                İptal
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {editingProduct ? 'Güncelle' : 'Oluştur'}
                            </Button>
                        </div>
                    </Form>
                </Modal>
            </div>
        </ProtectedLayout>
    );
};

export default Products; 